/* eslint-disable react/no-array-index-key */
import PropTypes from 'prop-types';
import React, {
  useContext, useRef, useState, useEffect,
} from 'react';
import ReactHtmlParser from 'react-html-parser';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

import { Alignment, LinkTargets } from '@powdr/constants';
import { AppContext } from '@powdr/context';
import { useSeason } from '@powdr/hooks';

import {
  BlurIcon,
  MobileScrollBlur,
  StyledUiTable,
  Table,
  TableBody,
  TableWrapper,
  Row,
  HeaderCell,
  DataCell,
  CellLink,
  CellIcon,
} from './styles';

const BUY_NOW = 'Buy Now';

const SearchString = {
  HTTP: 'http://',
  HTTPS: 'https://',
  SRC_HTTP: 'src="http',
};

export const UiTable = ({
  id = '',
  isFirstColumnBold = false,
  isFirstColumnCenter = false,
  isLastColumnCenter = false,
  isHorizontalScroll = false,
  parentColorProfile = null,
  componentSeason = null,
  tables,
}) => {
  const { isMobile } = useContext(AppContext);
  const { checkSeasonality } = useSeason();
  const colorProfile = parentColorProfile;
  const simpleBarRef = useRef();
  const [isFirstColumnVisible, setIsFirstColumnVisible] = useState(true);
  const [isLastColumnVisible, setIsLastColumnVisible] = useState(true);

  // Update blur edges based on scroll position
  useEffect(() => {
    if (!isHorizontalScroll || !isMobile || !simpleBarRef.current) return;

    const el = simpleBarRef.current.getScrollElement();

    const updateVisibility = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setIsFirstColumnVisible(scrollLeft === 0);
      setIsLastColumnVisible(scrollLeft + clientWidth >= scrollWidth);
    };

    updateVisibility();
    el.addEventListener('scroll', updateVisibility);

    // eslint-disable-next-line consistent-return
    return () => {
      el.removeEventListener('scroll', updateVisibility);
    };
  }, [isHorizontalScroll, isMobile]);

  const checkCellDataForSpecialValues = (cellValue) => {
    if (
      (cellValue.includes(SearchString.HTTP) || cellValue.includes(SearchString.HTTPS))
      && !cellValue.includes(SearchString.SRC_HTTP)
    ) {
      const arr = cellValue.split(',');
      let target = LinkTargets.SELF;
      let href = null;
      let buttonText = BUY_NOW;

      arr.forEach((item) => {
        const trimmed = item.trim();
        if (trimmed.includes(SearchString.HTTP) || trimmed.includes(SearchString.HTTPS)) {
          href = trimmed;
        } else if ([LinkTargets.SELF, LinkTargets.PARENT, LinkTargets.BLANK].includes(trimmed)) {
          target = trimmed;
        } else {
          buttonText = trimmed;
        }
      });

      return (
        <CellLink className="cell-button" colorProfile={colorProfile} href={href} target={target}>
          {buttonText}
        </CellLink>
      );
    }

    if (cellValue.includes('icon=')) {
      const arr = cellValue.split(',');
      const iconName = arr[0].replace('icon=', '');
      const iconColor = arr?.[1];
      return <CellIcon name={iconName} height="20" width="20" color={iconColor || undefined} colorProfile={colorProfile} />;
    }

    if (cellValue.includes('bold-text')) {
      return cellValue.split(',')[0];
    }

    return ReactHtmlParser(cellValue);
  };

  return (
    checkSeasonality(componentSeason) && (
      <StyledUiTable key={id} className="ui-table" colorProfile={colorProfile}>
        {(isMobile && isHorizontalScroll) && (
          <>
            {!isFirstColumnVisible && (
              <MobileScrollBlur side={Alignment.LEFT}>
                <BlurIcon
                  name="decoration-blur"
                  width="20"
                  maxHeight={simpleBarRef?.current?.getScrollElement()?.clientHeight}
                />
              </MobileScrollBlur>
            )}
            {!isLastColumnVisible && (
              <MobileScrollBlur side={Alignment.RIGHT}>
                <BlurIcon
                  name="decoration-blur"
                  width="20"
                  maxHeight={simpleBarRef?.current?.getScrollElement()?.clientHeight}
                />
              </MobileScrollBlur>
            )}
          </>
        )}

        {tables.map((table, tableIndex) => {
          const rows = Object.values(table?.rows).filter((i) => i !== null);
          if ((table?.rows.length === 1 && table?.rows[0][0] === '') || Object.values(table?.rows)[0] === null) return null;

          const tableContent = (
            <Table
              $isHorizontalScroll={isHorizontalScroll}
              $hasMultipleTables={tables.length > 1}
            >
              <TableBody colorProfile={colorProfile}>
                {rows.map((row, rowIndex) => (
                  <Row key={`row-${tableIndex}-${rowIndex}`} colorProfile={colorProfile}>
                    {row.map((tableCell, cellIndex) => (rowIndex === 0 ? (
                      <HeaderCell
                        key={`cell-${rowIndex}-${cellIndex}`}
                        isFirstColumnCenter={isFirstColumnCenter}
                        isLastColumnCenter={isLastColumnCenter}
                        isBoldCell={tableCell.includes('bold-text')}
                        colorProfile={colorProfile}
                        $isHideHeaderCell={row.every((x) => x === '')}
                      >
                        {checkCellDataForSpecialValues(tableCell)}
                      </HeaderCell>
                    ) : (
                      <DataCell
                        key={`cell-${rowIndex}-${cellIndex}`}
                        isFirstColumnBold={isFirstColumnBold}
                        isFirstColumnCenter={isFirstColumnCenter}
                        isLastColumnCenter={isLastColumnCenter}
                        isBoldCell={tableCell.includes('bold-text')}
                        colorProfile={colorProfile}
                      >
                        {checkCellDataForSpecialValues(tableCell)}
                      </DataCell>
                    )))}
                  </Row>
                ))}
              </TableBody>
            </Table>
          );

          return (
            <TableWrapper key={`table-${tableIndex}`} $isHorizontalScroll={isHorizontalScroll}>
              {isMobile && isHorizontalScroll ? (
                <SimpleBar ref={simpleBarRef} style={{ overflowX: 'scroll' }} autoHide={false}>
                  {tableContent}
                </SimpleBar>
              ) : (
                tableContent
              )}
            </TableWrapper>
          );
        })}
      </StyledUiTable>
    )
  );
};

UiTable.propTypes = {
  id: PropTypes.string,
  isFirstColumnBold: PropTypes.bool,
  isFirstColumnCenter: PropTypes.bool,
  isLastColumnCenter: PropTypes.bool,
  isHorizontalScroll: PropTypes.bool,
  tables: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  parentColorProfile: PropTypes.string,
  componentSeason: PropTypes.string,
};
